import _extends from "@babel/runtime/helpers/esm/extends";
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import { StyledTab } from './Tab.styles';

/**
 * The `Tabs.Tab` component is used by the `Tabs` and `Tabs.List` components.
 *
 * @extends ToggleButton
 * @since 8.10.0
 */
export var Tab = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  /**
   * Use `aria-selected` instead of `aria-pressed` as per WAI-ARIA guidelines:
   * https://www.w3.org/TR/wai-aria-practices-1.2/#wai-aria-roles-states-and-properties-21
   */
  var isPressed = props.defaultPressed || props.pressed;
  return /*#__PURE__*/React.createElement(StyledTab, _extends({
    "aria-pressed": undefined,
    "aria-selected": isPressed,
    ref: forwardedRef,
    role: "tab",
    size: "lg",
    tabIndex: isPressed ? '0' : '-1'
  }, props));
});
Tab.displayName = 'Tabs.Tab';
Tab.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * If you're using the `Tabs` parent,
   * this must match the `id` of the
   * `Tabs.Panel` associated with this tab.
   *
   * If no `aria-controls` is specified, one will automatically be generated by the
   * `Tabs.List` parent.
   */
  'aria-controls': PropTypes.string,
  /**
   * Indicate if this is the selected `Tabs.Tab` and use as an
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   *
   * A `Tabs.Tab` can use `pressed` or `defaultPressed`, but not both.
   *
   * @ignore
   */
  'defaultPressed': PropTypes.bool,
  /**
   * Set the `Tabs.Tab` as disabled.
   */
  'disabled': PropTypes.bool,
  /**
   * Add an `Icon` to the button.
   */
  'icon': PropTypes.node,
  /**
   * A unique identifier. If this `Tabs.Tab`'s `id` matches its
   * `Tabs`'s or `Tabs.List`'s
   * `selected` or `defaultSelected`, this `Tabs.Tab` will be selected.
   *
   * If you're using the `Tabs` parent, this identifier
   * must also match the `aria-labelledby` of its corresponding `Tabs.Panel`.
   */
  'id': PropTypes.string.isRequired,
  /**
   * A click handler that receives two arguments: the click event and
   * a boolean that reflects if this is the selected `Tabs.Tab`.
   */
  'onClick': PropTypes.func,
  /**
   * Indicate if this is the selected `Tabs.Tab` and use as a
   * [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   *
   * A `Tabs.Tab` can use `pressed` or `defaultPressed`, but not both.
   *
   * @ignore
   */
  'pressed': PropTypes.bool
} : {};